import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Check, Download, MapPin, CalendarDays, Users, Wallet, CreditCard, Loader2 } from "lucide-react";
import Footer from "@/components/Footer";
import SignatureBox from "@/components/SignatureBox";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

function DepositBlock({ q, token }) {
  const [loading, setLoading] = useState(false);
  if (q.deposit_paid) {
    return (
      <div className="mt-5 p-4 rounded-lg bg-emerald-500/10 border border-emerald-400/40" data-testid="deposit-paid">
        <div className="av-eyebrow" style={{ color: "#7DD3A0" }}>
          <Check size={12} className="inline mr-1" /> Acconto saldato
        </div>
        <p className="text-sm text-[#F7F4EE]/85 mt-1">
          € {Number(q.deposit_amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} già ricevuti.
          Grazie!
        </p>
      </div>
    );
  }
  if (!q.cost_total || q.cost_total <= 0 || !q.deposit_percent) return null;
  const amount = (q.cost_total * q.deposit_percent) / 100;
  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/quote-requests/public/${token}/deposit-checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };
  return (
    <div className="mt-5 p-4 rounded-lg border av-hairline" data-testid="deposit-cta">
      <div className="av-eyebrow">Prenota la data con un acconto</div>
      <p className="text-sm text-[#F7F4EE]/85 mt-1">
        Versa il <strong>{q.deposit_percent}%</strong> — €{" "}
        {amount.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — con
        pagamento sicuro Stripe. La data verrà bloccata a tuo nome.
      </p>
      <button
        onClick={start}
        disabled={loading}
        className="av-pill av-pill-gold mt-4"
        data-testid="deposit-pay-btn"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
        {loading ? "Attendi…" : "Paga l'acconto ora"}
      </button>
    </div>
  );
}

export default function QuotePublic() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/quote-requests/public/${token}`)
      .then((r) => setQ(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Preventivo non disponibile"));
  }, [token]);

  if (err) return <div className="pt-28 text-center text-[#A39E93]">{err}</div>;
  if (!q) return <div className="pt-28 text-center text-[#A39E93]">Caricamento…</div>;

  const money = (v) => (v ? `€ ${Number(v).toLocaleString("it-IT")}` : "—");

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="av-eyebrow" data-testid="public-eyebrow">
          Preventivo NMA Agency · Anteprima interattiva
        </span>
        <h1
          className="font-serif-display text-4xl sm:text-6xl mt-4 leading-tight"
          data-testid="public-event-name"
        >
          {q.event_name}
        </h1>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-[#A39E93]">
          {q.city && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={13} className="text-[#E5A93C]" /> {q.city}
              {q.venue ? ` · ${q.venue}` : ""}
            </span>
          )}
          {q.event_date && (
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays size={13} className="text-[#E5A93C]" /> {q.event_date}
            </span>
          )}
          {q.audience_size && (
            <span className="inline-flex items-center gap-1.5">
              <Users size={13} className="text-[#E5A93C]" /> {q.audience_size} persone
            </span>
          )}
          {q.budget && (
            <span className="inline-flex items-center gap-1.5">
              <Wallet size={13} className="text-[#E5A93C]" /> Budget {money(q.budget)}
            </span>
          )}
        </div>

        {q.admin_note && (
          <div className="av-card p-5 mt-8" data-testid="public-admin-note">
            <div className="av-eyebrow">Il pensiero di NMA</div>
            <p className="text-[#F7F4EE]/85 mt-2 italic whitespace-pre-line">
              "{q.admin_note}"
            </p>
          </div>
        )}

        {q.artists.length > 0 && (
          <>
            <h2 className="font-serif-display text-3xl mt-14">
              Artisti <span className="italic text-[#E5A93C]">proposti</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="public-artists">
              {q.artists.map((a) => (
                <article key={a.id} className="av-card overflow-hidden" data-testid={`public-artist-${a.id}`}>
                  {a.image_url && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="av-eyebrow text-[10px]">{a.discipline}</div>
                    <h3 className="font-serif-display text-xl mt-2">{a.name}</h3>
                    <p className="text-sm text-[#A39E93] mt-1">{a.city}</p>
                    <p className="text-sm text-[#F7F4EE]/70 mt-3 line-clamp-3">{a.bio}</p>
                    <div className="text-[#E5A93C] font-mono text-sm mt-4">
                      {a.price_from ? `da ${money(a.price_from)}` : "Su richiesta"}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {q.shows.length > 0 && (
          <>
            <h2 className="font-serif-display text-3xl mt-14">
              Spettacoli <span className="italic text-[#E5A93C]">proposti</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="public-shows">
              {q.shows.map((s) => (
                <article key={s.id} className="av-card overflow-hidden" data-testid={`public-show-${s.id}`}>
                  {s.image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="av-eyebrow text-[10px]">{s.category}</div>
                    <h3 className="font-serif-display text-xl mt-2">{s.title}</h3>
                    {s.duration_min && (
                      <p className="text-xs text-[#A39E93] mt-1">{s.duration_min} min</p>
                    )}
                    <p className="text-sm text-[#F7F4EE]/70 mt-3 line-clamp-3">{s.description}</p>
                    <div className="text-[#E5A93C] font-mono text-sm mt-4">
                      {s.price_from ? `da ${money(s.price_from)}` : "Su richiesta"}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        <div className="av-card p-6 mt-12 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="av-eyebrow">Prossimo passo</div>
            <p className="text-[#F7F4EE]/85 mt-1 text-sm max-w-md">
              Scarica il PDF completo o firma digitalmente qui sotto per confermare.
            </p>
          </div>
          <a
            href={`${API}/quote-requests/public/${token}/pdf`}
            className="av-pill av-pill-gold"
            data-testid="public-download-pdf"
          >
            <Download size={14} /> Scarica il PDF
          </a>
        </div>

        {q.cost_items && q.cost_items.length > 0 && (
          <div className="av-card p-6 mt-8" data-testid="public-cost-items">
            <div className="av-eyebrow">Voci di costo</div>
            <table className="w-full mt-4 text-sm">
              <tbody>
                {q.cost_items.map((it, i) => (
                  <tr key={`${it.label || "row"}-${i}`} className="border-b av-hairline">
                    <td className="py-2 text-[#F7F4EE]/90">{it.label}</td>
                    <td className="py-2 text-right font-mono text-[#F7F4EE]">
                      € {Number(it.amount || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="pt-3 font-serif-display text-lg text-[#E5A93C]">Totale</td>
                  <td className="pt-3 text-right font-mono text-lg text-[#E5A93C]">
                    € {Number(q.cost_total || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {q.signed_at ? (
          <div className="av-card p-6 mt-8 border-l-4 border-l-emerald-400" data-testid="public-signed-block">
            <div className="av-eyebrow" style={{ color: "#7DD3A0" }}>
              <Check size={12} className="inline mr-1" /> Preventivo firmato
            </div>
            <p className="text-[#F7F4EE]/90 mt-2">
              Firmato da <strong>{q.signed_by}</strong> il{" "}
              {new Date(q.signed_at).toLocaleString("it-IT")}
            </p>
            {q.signature_png && (
              <img
                src={q.signature_png}
                alt="Firma"
                className="mt-3 max-h-24 rounded bg-[#F7F4EE] p-2"
              />
            )}
            <DepositBlock q={q} token={token} />
          </div>
        ) : (
          <SignatureBox token={token} onSigned={(fresh) => setQ(fresh)} />
        )}
      </section>
      <Footer />
    </div>
  );
}
